import { useMemo } from 'react';

import { CartItem, usePrefetchShowCart, useShowCart } from '@givebacks/api/store';
import {
  FullOrderModalStack,
  FullOrderModalStackModals,
  useFullOrderModalStack,
} from 'core/Components';
import { CellContext, useCrowdFundingTranslations } from 'core/Hooks';
import { Table } from 'core/Packages';
import {
  PAYMENT_METHODS_MAPPING,
  centsToDollar,
  columnsCreator,
  getDate,
  toTitleCase,
} from 'core/Utils';
import { Stack, Text } from 'ui';
import { IconReceipt } from 'ui/Icons';

import { useCrowdfundingProvider } from '../../../../../Context/crowdfunding/Crowdfunding.provider';
import { donationHistoryColumnHelper } from './donationHistoryColumnHelper';

const CartActions = (props: CellContext<CartItem, unknown>) => {
  const { causeId } = useCrowdfundingProvider();
  const { stack, open: openStack } = useFullOrderModalStack();

  const { data: cartData } = useShowCart({ cartId: props.row.original.cart?.uuid, causeId });

  const isViewFullOrderVisible = useMemo(() => cartData?.status != 'pledged', [cartData?.status]);

  const prefetchCart = usePrefetchShowCart({
    cartId: props.row.original.cart?.uuid,
    causeId,
  });

  if (!cartData) return null;
  return (
    <>
      <Table.RowAction onMouseEnter={prefetchCart}>
        <Table.RowAction.Option
          label="View Full Order"
          icon={IconReceipt}
          onClick={() => {
            openStack(FullOrderModalStackModals.FullOrder);
          }}
          visible={isViewFullOrderVisible}
        />
      </Table.RowAction>

      <FullOrderModalStack stack={stack} order={cartData} />
    </>
  );
};

const DonorNameHeader = () => {
  const { causeId } = useCrowdfundingProvider();
  const { t } = useCrowdFundingTranslations(causeId);
  return <>{t('Donor')} Name</>;
};
const DonationAmountHeader = () => {
  const { causeId } = useCrowdfundingProvider();
  const { t } = useCrowdFundingTranslations(causeId);
  return <>{t('Donation')} Amount</>;
};

const columns = [
  donationHistoryColumnHelper.accessor('cart.first_name', {
    header: () => <DonorNameHeader />,
    id: 'carts.first_name',
    cell: (props) => (
      <Stack gap={0}>
        <Text fz="xs">
          {props.row.original.cart?.first_name} {props.row.original.cart?.last_name}
        </Text>
        <Text fz={'xxxs'}>{props.row.original.cart?.email}</Text>
      </Stack>
    ),
  }),

  donationHistoryColumnHelper.accessor((row) => getDate(row.created_at), {
    header: 'Date',
    id: 'created_at',
  }),

  donationHistoryColumnHelper.accessor((row) => row.cart_id, {
    header: 'Order number',
    id: 'cart_id',
  }),

  donationHistoryColumnHelper.accessor('total', {
    header: () => <DonationAmountHeader />,
    cell: (props) => centsToDollar(props.getValue()),
  }),

  donationHistoryColumnHelper.accessor('total_to_cause', {
    header: 'Your Share',
    id: 'your_share',
    cell: (props) => centsToDollar(props.getValue()),
    size: 100,
    enableSorting: false,
  }),

  donationHistoryColumnHelper.accessor((row) => toTitleCase(row.cart?.status ?? ''), {
    header: 'Status',
    enableSorting: false,
    id: 'carts.status',
  }),

  donationHistoryColumnHelper.accessor(
    (row) => (row.cart?.payment_method ? PAYMENT_METHODS_MAPPING[row.cart?.payment_method] : ''),
    {
      header: 'Payment Method',
      id: 'recurring',
    }
  ),
  donationHistoryColumnHelper.accessor((row) => row.designation, {
    header: 'Donation Designation',
    id: 'designation',
    enableSorting: false,
  }),
  donationHistoryColumnHelper.display({
    id: 'actions',
    cell: (props) => <CartActions {...props} />,
    size: 40,
  }),
];

const exportColumnsCreator = columnsCreator<CartItem>();

const exportDonationCartColumns = [
  exportColumnsCreator.create({
    header: 'First Name',
    cell: (row) => row.cart?.first_name,
  }),
  exportColumnsCreator.create({
    header: 'Last Name',
    cell: (row) => row.cart?.last_name,
  }),
  exportColumnsCreator.create({
    header: 'Email',
    cell: (row) => row.cart?.email,
  }),

  exportColumnsCreator.create({
    header: 'Date',
    columnFormat: 'dateTime',
    cell: (row) => row.cart?.purchased_at,
  }),

  exportColumnsCreator.create({
    header: 'Order Number',
    cell: (row) => row.cart?.uuid,
  }),

  exportColumnsCreator.create({
    header: 'Donation Amount',
    columnFormat: 'currency_formatted',
    cell: (row) => row.total,
  }),

  exportColumnsCreator.create({
    header: 'Your Share',
    columnFormat: 'currency_formatted',
    cell: (row) => row.total_to_cause,
  }),

  exportColumnsCreator.create({
    header: 'Donation Status',
    cell: (row) => toTitleCase(row.cart?.status ?? ''),
  }),

  exportColumnsCreator.create({
    header: 'Payment Method',
    cell: (row) => {
      return row.cart?.payment_method
        ? PAYMENT_METHODS_MAPPING[row.cart?.payment_method]
        : row.cart?.payment_method;
    },
  }),

  exportColumnsCreator.create({
    header: 'Donation Level',

    cell: (row) => row.designation,
  }),
];

export { columns, exportDonationCartColumns };
